const CONSENT_KEY = 'platforma_platform_tracking_consent_v1';

function hasConsent() {
  try { return localStorage.getItem(CONSENT_KEY) === 'accepted'; } catch (_error) { return false; }
}

function cleanPath(value) {
  const path = String(value || '/').split(/[?#]/, 1)[0] || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') : '/';
}

function bestRules(config, path) {
  const targets = new Map((config.targets || []).map((target) => [target.key, target]));
  const matches = (config.rules || []).map((rule) => ({ ...rule, target: targets.get(rule.targetKey) }))
    .filter(({ target }) => target && (target.match === 'exact'
      ? path === target.path
      : target.path === '/' || path === target.path || path.startsWith(`${target.path}/`)));
  const selected = new Map();
  for (const rule of matches) {
    const score = (rule.target.match === 'exact' ? 10000 : 0) + rule.target.path.length;
    if (!selected.has(rule.provider) || selected.get(rule.provider).score < score) selected.set(rule.provider, { ...rule, score });
  }
  return [...selected.values()];
}

function loadScript(id, src) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function ensureGtag(tagId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  if (!window.__platformaGtagStarted) {
    window.__platformaGtagStarted = true;
    window.gtag('js', new Date());
  }
  loadScript(`platforma-gtag-${tagId}`, `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`);
  window.gtag('config', tagId, { send_page_view: false });
}

function ensureMeta(pixelId) {
  if (!window.fbq) {
    const fbq = function fbq() { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
    fbq.push = fbq; fbq.loaded = true; fbq.version = '2.0'; fbq.queue = [];
    window.fbq = fbq;
    loadScript('platforma-meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');
  }
  window.fbq('init', pixelId);
}

function ensureX(pixelId) {
  if (!window.twq) {
    const twq = function twq() { twq.exe ? twq.exe.apply(twq, arguments) : twq.queue.push(arguments); };
    twq.version = '1.1'; twq.queue = [];
    window.twq = twq;
    loadScript('platforma-x-pixel', 'https://static.ads-twitter.com/uwt.js');
  }
  window.twq('config', pixelId);
}

function ensureTikTok(pixelId) {
  if (!window.ttq) {
    const ttq = [];
    const methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent'];
    ttq.methods = methods;
    ttq.setAndDefer = (target, method) => { target[method] = (...args) => target.push([method, ...args]); };
    methods.forEach((method) => ttq.setAndDefer(ttq, method));
    ttq.instance = (id) => {
      const instance = ttq._i[id] || [];
      methods.forEach((method) => { if (!instance[method]) ttq.setAndDefer(instance, method); });
      return instance;
    };
    ttq.load = (id) => {
      const instance = ttq.instance(id);
      ttq._i[id] = instance;
      instance._u = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      instance._t = Date.now();
      loadScript(`platforma-tiktok-pixel-${id}`, `${instance._u}?sdkid=${encodeURIComponent(id)}&lib=ttq`);
    };
    ttq._i = {};
    window.TiktokAnalyticsObject = 'ttq';
    window.ttq = ttq;
  }
  if (!window.ttq._i[pixelId]) window.ttq.load(pixelId);
}

function ensureGtm(tagId) {
  window.dataLayer = window.dataLayer || [];
  if (!document.getElementById(`platforma-gtm-${tagId}`)) {
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    loadScript(`platforma-gtm-${tagId}`, `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(tagId)}`);
  }
}

export function installPlatformaConversionTracking(router) {
  let config = null;
  let lastSignature = '';

  const track = () => {
    if (!config || !hasConsent()) return;
    const path = cleanPath(router.currentRoute.value.path || window.location.pathname);
    const rules = bestRules(config, path);
    const signature = `${path}:${rules.map((rule) => `${rule.provider}:${rule.targetKey}`).sort().join('|')}`;
    if (!rules.length || signature === lastSignature) return;
    lastSignature = signature;
    const pageLocation = `${window.location.origin}${path}`;

    for (const rule of rules) {
      const tagId = config.integrations?.[rule.provider]?.tagId;
      if (!tagId) continue;
      const eventName = `platforma_${rule.target.eventKey}`;
      const common = { event_key: rule.target.eventKey, page_location: pageLocation, page_title: document.title };
      if (rule.provider === 'google-analytics') {
        ensureGtag(tagId);
        window.gtag('event', 'page_view', { page_location: pageLocation, page_title: document.title });
        window.gtag('event', eventName, common);
      } else if (rule.provider === 'google-ads') {
        ensureGtag(tagId);
        window.gtag('event', 'conversion', { ...common, send_to: `${tagId}/${rule.conversionId}` });
      } else if (rule.provider === 'google-tag-manager') {
        ensureGtm(tagId);
        window.dataLayer.push({ event: eventName, ...common });
      } else if (rule.provider === 'meta-ads') {
        ensureMeta(tagId);
        window.fbq('trackSingle', tagId, 'PageView', common);
        window.fbq('trackSingleCustom', tagId, eventName, common);
      } else if (rule.provider === 'tiktok-ads') {
        ensureTikTok(tagId);
        window.ttq.instance(tagId).track('ViewContent', common);
      } else if (rule.provider === 'x-ads') {
        ensureX(tagId);
        window.twq('event', rule.conversionId, common);
      }
    }
  };

  fetch('/api/platforma-conversion-tracking', { credentials: 'same-origin' })
    .then(async (response) => ({ response, payload: await response.json() }))
    .then(({ response, payload }) => {
      if (!response.ok || payload.success === false) return;
      config = payload.config;
      window.__platformaTrackingAvailable = Boolean(config.rules?.length);
      window.dispatchEvent(new CustomEvent('platforma:platform-tracking-config-ready', { detail: { active: Boolean(config.rules?.length) } }));
      track();
    })
    .catch(() => { /* Tracking must never interrupt the Creator App. */ });

  router.afterEach(() => window.setTimeout(track, 0));
  window.addEventListener('platforma:platform-tracking-consent-changed', () => {
    lastSignature = '';
    track();
  });
}
